import { ComponentProps } from 'react';
import { useSearchParams } from 'react-router';
import { ArrowDown, ArrowUp, ChevronsUpDown, X } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface Props extends ComponentProps<'div'> {
  sortKey: string;
  title: string;
}

export function DataTableColumnHeader({ className, sortKey, title }: Props) {
  const [searchParams, setSearchParams] = useSearchParams();
  const sortBy = searchParams.get('sort_by');
  const sortDir = searchParams.get('sort_dir');
  const keyMatch = sortBy === sortKey;

  function onClick(value: 'asc' | 'desc' | 'clear') {
    setSearchParams((prev) => {
      if (value === 'clear') {
        prev.delete('sort_by');
        prev.delete('sort_dir');
      } else {
        prev.set('sort_by', sortKey);
        prev.set('sort_dir', value);
      }

      return prev;
    });
  }

  return (
    <div className={cn('flex items-center space-x-2', className)}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="data-[state=open]:bg-accent -ml-3 h-8"
          >
            <span className="text-xs">{title}</span>
            {keyMatch && sortDir === 'desc' ? (
              <ArrowDown />
            ) : keyMatch && sortDir === 'asc' ? (
              <ArrowUp />
            ) : (
              <ChevronsUpDown />
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuItem onClick={() => onClick('asc')}>
            <ArrowUp className="text-muted-foreground/70 size-3.5" />
            Asc
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onClick('desc')}>
            <ArrowDown className="text-muted-foreground/70 size-3.5" />
            Desc
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => onClick('clear')}>
            <X className="text-muted-foreground/70 size-3.5" />
            Clear
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
